import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { api, onSessionExpired, refreshAccessToken, setAccessToken } from '@/lib/api';
import { can as canAccess, type PageKey } from '@/lib/permissions';
import type { User } from '@/lib/types';

interface AuthValue {
  user: User | null;
  status: 'checking' | 'authenticated' | 'anonymous';
  signIn: (login: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** The top admin — reaches everything regardless of its permission list. */
  isSuperAdmin: boolean;
  /** May the signed-in user open a given dashboard page? */
  can: (page: PageKey) => boolean;
  /** Website content shortcut — any of the content pages. */
  canManageWebsite: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthValue['status']>('checking');

  /*
   * On boot there is no access token in memory — by design, it is never
   * persisted. The refresh cookie is what restores the session, so a reload
   * costs one round trip and nothing is left in storage for an XSS to find.
   */
  useEffect(() => {
    let cancelled = false;
    refreshAccessToken()
      .then(async (token) => {
        if (cancelled) return;
        if (!token) {
          setStatus('anonymous');
          return;
        }
        const { data } = await api.get<{ user: User }>('/auth/me');
        if (cancelled) return;
        setUser(data.user);
        setStatus('authenticated');
      })
      .catch(() => {
        if (!cancelled) setStatus('anonymous');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    onSessionExpired(() => {
      setAccessToken(null);
      setUser(null);
      setStatus('anonymous');
    });
  }, []);

  const signIn = useCallback(async (login: string, password: string) => {
    const { data } = await api.post<{ accessToken: string; user: User }>('/auth/login', {
      email: login,
      password,
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    setStatus('authenticated');
    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const { data } = await api.post<{ accessToken: string; user: User }>('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
  }, []);

  const value = useMemo<AuthValue>(() => {
    const actor = user?.role
      ? { isSuperAdmin: user.role.isSuperAdmin, permissions: user.role.permissions }
      : null;
    const can = (page: PageKey) => canAccess(actor, page);
    return {
      user,
      status,
      signIn,
      signOut,
      changePassword,
      isSuperAdmin: actor?.isSuperAdmin ?? false,
      can,
      canManageWebsite:
        can('articles') || can('services') || can('pages') || can('categories') || can('redirects'),
    };
  }, [user, status, signIn, signOut, changePassword]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
